import type Phaser from "phaser";
import { ArtPipeline, type CardinalDir } from "../../../art";
import { hexToColor } from "../../../data/infinite-war-catalog";
import { infiniteWarCatalog } from "../../../data/infinite-war-catalog";
import {
  roleStats,
  type InfiniteWarState,
  type IwTeam,
} from "../infinite-war-rules";
import {
  UnitRig,
  createHpBar,
  infiniteWarGripPack,
  updateHpBar,
  type HpBarParts,
} from "../../../view/unit-rig";
import {
  ART_PACK,
  HP_FADE_MS,
  HP_SHOW_MS,
  RIG,
  VIEW,
  WALK_ENTER,
  factionColor,
} from "./layout";

export type UnitView = {
  root: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig;
  hp: HpBarParts;
};

export type DemigodView = {
  root: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig;
};

export function facingFor(unit: {
  facingX?: number;
  facingY?: number;
  intentVx?: number;
  intentVy?: number;
}): CardinalDir {
  const x = unit.facingX ?? unit.intentVx ?? 0;
  const y = unit.facingY ?? unit.intentVy ?? 0;
  if (Math.abs(x) > Math.abs(y)) return x >= 0 ? "e" : "w";
  return y >= 0 ? "s" : "n";
}

export type UnitsViewHost = {
  scene: Phaser.Scene;
  unitViews: Map<number, UnitView>;
  demigodViews: Map<IwTeam, DemigodView>;
  unitHpSeen: Map<number, number>;
  unitHpRevealUntil: Map<number, number>;
  attackMotions: Map<number, { until: number; dx: number; dy: number }>;
  trailBudget: { left: number };
  demigodGfx: Phaser.GameObjects.Graphics;
  veilGfx: Phaser.GameObjects.Graphics;
};

export function syncAliveUnits(
  host: UnitsViewHost,
  state: InfiniteWarState,
): void {
  const alive = new Set<number>();
  for (const unit of state.units) {
    if (unit.phase !== "alive") continue;
    alive.add(unit.id);
    const stats = roleStats(unit.roleId);
    const facing = facingFor(unit);
    const attack = host.attackMotions.get(unit.id);
    const attacking = !!attack && attack.until > host.scene.time.now;
    if (attack && !attacking) host.attackMotions.delete(unit.id);
    const animState = attacking
      ? "attack"
      : Math.hypot(unit.intentVx, unit.intentVy) >= WALK_ENTER
        ? "walk"
        : "idle";
    const animTime = attacking
      ? Math.max(0, 0.15 - (attack!.until - host.scene.time.now) / 1000)
      : state.time;
    const affiliation = unit.team === "south" ? "ally" : "enemy";
    const { key } = ArtPipeline.ensureAnimFrame(
      host.scene,
      ART_PACK,
      unit.roleId,
      affiliation,
      animState,
      animTime,
      facing,
    );
    let view = host.unitViews.get(unit.id);
    if (!view) {
      const body = host.scene.add
        .image(0, 0, key)
        .setDisplaySize(stats.displaySize, stats.displaySize);
      const rig = new UnitRig(
        host.scene,
        body,
        unit.roleId,
        RIG,
        WALK_ENTER,
        undefined,
        infiniteWarGripPack,
      );
      const hp = createHpBar(host.scene, {
        barW: Math.max(28, stats.displaySize * 0.9),
        barH: VIEW.layout.hpBarHeight,
        y: stats.displaySize * (VIEW.layout.hpBarFootFrac ?? 0.42),
        bgColor: hexToColor(VIEW.colors.hpBg),
        fillColor: factionColor(unit.team, "hp"),
        damageFlash: true,
      });
      hp.bg.setVisible(false);
      hp.ghost.setVisible(false);
      hp.fill.setVisible(false);
      const root = host.scene.add
        .container(unit.x, unit.y, [rig.container, hp.bg, hp.ghost, hp.fill])
        .setDepth(6);
      view = { root, body, rig, hp };
      host.unitViews.set(unit.id, view);
      host.unitHpSeen.set(unit.id, unit.hp);
    }
    view.rig.setBodyTexture(key);
    view.rig.update(
      {
        animState,
        animTime,
        intentVx: unit.intentVx,
        intentVy: unit.intentVy,
        facing: (unit.facingX ?? 0) >= 0 ? 1 : -1,
        aimAngle: Math.atan2(unit.facingY ?? 1, unit.facingX ?? 0),
        facingDir: facing,
        lockBodyFlip: true,
        dying: false,
      },
      host.trailBudget,
      host.scene,
      { x: unit.x, y: unit.y },
    );
    view.root.setPosition(unit.x, unit.y);
    syncUnitHpChrome(host, unit.id, unit.hp, unit.maxHp, unit.team, view.hp);
  }
  for (const [id, view] of host.unitViews) {
    if (!alive.has(id)) {
      view.root.destroy();
      host.unitViews.delete(id);
      host.unitHpSeen.delete(id);
      host.unitHpRevealUntil.delete(id);
    }
  }
}

export function syncUnitHpChrome(
  host: UnitsViewHost,
  id: number,
  hp: number,
  maxHp: number,
  team: IwTeam,
  parts: HpBarParts,
): void {
  const prev = host.unitHpSeen.get(id);
  host.unitHpSeen.set(id, hp);
  if (prev != null && hp < prev - 0.01) {
    host.unitHpRevealUntil.set(id, host.scene.time.now + HP_SHOW_MS);
  }
  const until = host.unitHpRevealUntil.get(id) ?? 0;
  const remain = until - host.scene.time.now;
  const show = remain > 0;
  const fade =
    show && remain < HP_FADE_MS ? Math.max(0.15, remain / HP_FADE_MS) : show ? 1 : 0;
  updateHpBar(parts, {
    ratio: hp / maxHp,
    visible: show,
    fillColor: factionColor(team, "hp"),
  });
  parts.bg.setAlpha(fade * 0.8);
  parts.fill.setAlpha(fade);
  parts.ghost.setAlpha(fade * 0.85);
}

export function drawDemigods(host: UnitsViewHost, state: InfiniteWarState): void {
  const g = host.demigodGfx;
  g.clear();
  for (const team of ["north", "south"] as const) {
    const demigod = state.demigods[team];
    const stats = roleStats(demigod.roleId);
    const color = factionColor(team, "veil");
    g.fillStyle(color, 0.055).fillCircle(demigod.x, demigod.y, stats.range);
    g.lineStyle(1, color, 0.25).strokeCircle(demigod.x, demigod.y, stats.range);
    const facing: CardinalDir = team === "south" ? "n" : "s";
    const affiliation = team === "south" ? "ally" : "enemy";
    const { key } = ArtPipeline.ensureAnimFrame(
      host.scene,
      ART_PACK,
      demigod.roleId,
      affiliation,
      "idle",
      state.time,
      facing,
    );
    let view = host.demigodViews.get(team);
    if (!view) {
      const body = host.scene.add
        .image(0, 0, key)
        .setDisplaySize(stats.displaySize, stats.displaySize);
      const rig = new UnitRig(
        host.scene,
        body,
        demigod.roleId,
        RIG,
        WALK_ENTER,
        undefined,
        infiniteWarGripPack,
      );
      const root = host.scene.add.container(demigod.x, demigod.y, [rig.container]).setDepth(6);
      view = { root, body, rig };
      host.demigodViews.set(team, view);
    }
    view.rig.setBodyTexture(key);
    view.rig.update(
      {
        animState: "idle",
        animTime: state.time,
        intentVx: 0,
        intentVy: 0,
        facing: team === "south" ? -1 : 1,
        aimAngle: team === "south" ? -Math.PI / 2 : Math.PI / 2,
        facingDir: facing,
        lockBodyFlip: true,
        dying: false,
      },
      host.trailBudget,
      host.scene,
      { x: demigod.x, y: demigod.y },
    );
    view.root.setPosition(demigod.x, demigod.y);
  }
}

export function drawVeilSouls(host: UnitsViewHost, state: InfiniteWarState): void {
  const g = host.veilGfx;
  g.clear();
  for (const unit of state.units) {
    if (unit.phase !== "soul" || !unit.killerTeam) continue;
    const progress = 1 - unit.soulRemain / infiniteWarCatalog.units.soulGrantDelaySec;
    const color = factionColor(unit.killerTeam, "veil");
    const glow = 10 + Math.sin(state.time * 8 + unit.id) * 2;
    const rise = progress * 12;
    g.fillStyle(color, 0.14).fillCircle(unit.x, unit.y - rise, glow * 1.6);
    g.lineStyle(2, color, 0.9).strokeCircle(unit.x, unit.y - rise, glow);
    g.fillStyle(color, 0.7).fillCircle(unit.x, unit.y - rise, 4);
  }
}

export function noteAttackMotion(
  host: UnitsViewHost,
  unitId: number,
  dx: number,
  dy: number,
): void {
  host.attackMotions.set(unitId, {
    until: host.scene.time.now + 150,
    dx,
    dy,
  });
}

export function destroyUnitViews(host: UnitsViewHost): void {
  for (const view of host.unitViews.values()) view.root.destroy();
  host.unitViews.clear();
  host.unitHpSeen.clear();
  host.unitHpRevealUntil.clear();
  for (const view of host.demigodViews.values()) view.root.destroy();
  host.demigodViews.clear();
}
